//
// # PDK Definition - Process Design Kit
//

import type {
  Color,
  DrmCite,
  MetalLayer,
  ViaDefinition,
  ViaExports,
} from "./metal";
import type { BuiltinSymbolType } from "./schematic";
export type { Color, MetalLayer, ViaDefinition, ViaExports };

// ============================================================================
// PDK Definition
// ============================================================================

/**
 * One GenAlpha device -> the OpenAccess cell that implements it.
 *
 * Exactly ONE of `generator` / `builtin` identifies the GenAlpha side; an entry
 * with both or neither is ignored rather than guessed at.
 *
 * The VIEW is deliberately not recorded — it follows from how the device is
 * referenced (schematic wants `symbol`, layout wants `layout`), so pinning it
 * here would make one of the two wrong.
 */
export interface DeviceMapping {
  /** GenAlpha generator name within this PDK, e.g. "nwres_b". */
  generator?: string;
  /** GenAlpha builtin id, e.g. "nmos", "res". */
  builtin?: string;
  /** OpenAccess library holding the cell, e.g. "cmos14lpp". */
  lib: string;
  /** OpenAccess cell name, e.g. "nwres_b". Often but not always the generator name. */
  cell: string;
  /**
   * Parameter values that select THIS cell among several implementing the same
   * generator. Absent means it is the generator's only cell.
   *
   * A foundry can spend a cell name on what GenAlpha spends a parameter on:
   * `gf12/nmos` at `vt: "lvt"` is `cmos14lpp/lvtnfet_stack`, and so on for all
   * four threshold flavours.
   *
   * Read backwards the cell IS the evidence for these values, so an imported
   * `lvtnfet_stack` recovers `vt: "lvt"` from the entry that matched — which is
   * why the discriminator lives beside the cell rather than in its own lookup.
   */
  when?: Record<string, string | number | boolean>;
  /**
   * How this device's GenAlpha parameters are named and typed in OpenAccess.
   *
   * OA validates a written parameter against the PCell's declared list and
   * refuses both an unknown NAME and a mismatched DATATYPE, so GenAlpha can
   * carry neither its own vocabulary nor a guessed variant across
   * (`cmos14lpp/nfet_stack` declares `nStack` a string, `dumNumUserLeft` an
   * int). Both facts are the foundry's, so both live here (G23, M4).
   *
   * A GA parameter may appear twice, for a device that splits it across two OA
   * parameters — `ndum` is per-side, and the foundry counts each side. Read
   * backwards, the two must agree.
   *
   * A parameter absent from this list does not travel, and the export records
   * that rather than passing over it in silence.
   */
  params?: Array<DeviceParamMapping>;
}

/**
 * One GenAlpha generator parameter <-> one OpenAccess PCell parameter.
 *
 * ASSERTED by whoever writes the PDK metadata, from the two sides' names and
 * descriptions. That they draw the same geometry for the same value is not
 * established by this table, nor anywhere else yet (G23 step 2).
 */
export interface DeviceParamMapping {
  /** GenAlpha generator parameter name, e.g. "leftEndCap". */
  ga: string;
  /**
   * The GenAlpha-side type. Recorded rather than inferred: the pair
   * (gaType, oaType) is what makes the correspondence invertible. `nser` is a
   * GA number stored as the OA string `nStack = "3"`, and reading `"3"` back
   * without being told would have to guess between 3 and "3".
   */
  gaType: "string" | "number" | "boolean";
  /** OpenAccess PCell parameter name, e.g. "endPolyLeft". */
  oa: string;
  /**
   * The variant the PCell declares for it. OA refuses a write whose datatype
   * differs, so this is a fact about the foundry cell, not a preference.
   */
  oaType: "string" | "int" | "double" | "bool";
  /**
   * The two spellings, when a GA boolean is carried as an OA string — how the
   * gf12 FETs do it (`endPolyLeft = "TRUE"`). Both required together; either
   * alone is ignored, since a half-defined encoding cannot be inverted.
   */
  trueText?: string;
  falseText?: string;
}

export interface PDK {
  id: string;
  /** Logical PDK name used for lookups (e.g., "gf12") */
  name: string;
  /** Human-readable display name (e.g., "GlobalFoundries 12LP") */
  displayName?: string;
  foundry: string;
  /**
   * Process node in nanometers (nm)
   * @type int
   */
  processNode: number;
  version: string;

  /**
   * Manufacturing grid in nanometers (nm)
   * @type int
   */
  manufacturingGrid: number;

  stacks: Array<string>;

  layers: Array<PDKLayer>;

  /**
   * The GDS (layer, datatype) each layer/purpose streams to, for every stack;
   * a stack's own `layerMap` overrides it. Mirrors `schema::layer_map`.
   */
  layerMap: Array<StreamRow>;
  /** The layer/purposes deliberately left out of `layerMap`. */
  layerMapCoverage?: LayerMapCoverage;

  /**
   * Map from PDK-specific generator/device names to builtin symbol types.
   * Used to identify device types for symmetry inference and other analysis.
   * e.g., { "nmos": "nmos", "pmos": "pmos", "rmres": "res", "mimcap": "cap" }
   */
  symbolTypeMap?: Record<string, BuiltinSymbolType>;

  /**
   * How this PDK's devices are named in OpenAccess.
   *
   * GenAlpha names a device by its generator (`nwres_b` in `gf12`); OA needs a
   * (lib, cell), and the names differ — the OA library holding `nwres_b` is
   * `cmos14lpp`. Without this an export names a library nobody has heard of,
   * the instance resolves to nothing, and the device comes out missing while
   * the export reports success.
   *
   * Mirrors the `layers[].exports` pattern. Absent means the provisional
   * by-name mapping in `OaExport/remap.ts` applies.
   */
  devices?: Array<DeviceMapping>;

  /** Front-end rules that placement needs, from the PDK's design-rule manual. */
  frontend?: Frontend;
  /** The place-and-route values the manual does not state, for the PDK and its stacks. */
  drmCoverage?: DrmCoverage;
}

// ============================================================================
// Stack Definition
// ============================================================================

export interface Stack {
  id: string;
  name: string;
  description?: string;

  /**
   * Base layer pitches [x, y] in nanometers (nm)
   * @type int
   */
  base: [number, number];
  /**
   * Total number of metal layers
   * @type int
   */
  totalLayers: number;
  metalLayers: Array<MetalLayer>;

  vias: Array<ViaDefinition>;

  /**
   * This stack's stream rows: those that differ from, or add to, the PDK's
   * `layerMap`, such as metals whose datatype depends on their metal type.
   */
  layerMap?: Array<StreamRow>;

  /**
   * Dielectric properties for capacitance calculations.
   * Optional - if not specified, default values will be used.
   */
  dielectric?: {
    /**
     * Relative permittivity (dielectric constant) of the inter-metal dielectric.
     * Typical values: ~3.0-4.0 for low-k dielectrics, ~3.9 for SiO2.
     */
    k: number;
  };

  /**
   * Device-level electrical parameters for parasitic extraction.
   * Used to model contact resistance, diffusion resistance, and junction capacitance
   * at transistor terminals (source/drain/gate connections).
   */
  deviceParasitics?: DeviceParasitics;
}

// ============================================================================
// Device Parasitics (for transistor terminal R/C extraction)
// ============================================================================

/**
 * Electrical parameters for device-level parasitic extraction.
 *
 * These parameters model the resistance and capacitance at transistor terminals
 * (source, drain, gate) where metal connects to active silicon. Used by PEX to
 * compute contact resistance, diffusion resistance, and junction capacitance.
 *
 * Values are typically extracted from foundry SPICE model files (e.g., HSPICE
 * `.inc` files containing `rcont`, `rsh`, junction capacitance parameters).
 */
export interface DeviceParasitics {
  // -------------------------------------------------------------------------
  // Contact Resistance
  // -------------------------------------------------------------------------

  /**
   * Contact resistivity in ohm·µm² (Ω·µm²).
   * Resistance per contact = resistivity / contact_area.
   * Typical GF12LP value: ~0.223 Ω·µm² (223e-12 Ω·m² = 0.223 Ω·µm²)
   */
  contactResistivityOhmUm2?: number;

  /**
   * Contact size in nm (assumes square contacts). Used with `contactResistivity`
   * to compute per-contact resistance when not explicitly provided.
   * @type int
   */
  contactSizeNm?: number;

  /**
   * Explicit contact resistance per contact in Ohms (Ω).
   * If provided, takes precedence over computed value from resistivity.
   */
  contactResistanceOhms?: number;

  // -------------------------------------------------------------------------
  // Diffusion Sheet Resistance
  // -------------------------------------------------------------------------

  /**
   * N+ diffusion sheet resistance in Ohms per square (Ω/□).
   * Used for NMOS source/drain resistance: R = Rsh × L / W
   * Typical GF12LP value: ~170 Ω/□
   */
  nDiffusionSheetResistance?: number;

  /**
   * P+ diffusion sheet resistance in Ohms per square (Ω/□).
   * Used for PMOS source/drain resistance: R = Rsh × L / W
   * Typical GF12LP value: ~120 Ω/□
   */
  pDiffusionSheetResistance?: number;

  /**
   * Silicided diffusion sheet resistance in Ohms per square (Ω/□).
   * Lower than non-silicided; used when silicide is present.
   */
  silicideSheetResistance?: number;

  // -------------------------------------------------------------------------
  // Poly Gate Resistance
  // -------------------------------------------------------------------------

  /**
   * Polysilicon gate sheet resistance in Ohms per square (Ω/□).
   * Used for gate resistance: R = Rsh × L / W
   */
  polySheetResistance?: number;

  // -------------------------------------------------------------------------
  // Junction Capacitance (Source/Drain to Substrate)
  // -------------------------------------------------------------------------

  /**
   * N+ junction area capacitance in fF/µm² (NMOS source/drain to P-well).
   * Total C_area = cj × junction_area
   */
  nJunctionCapFfPerUm2?: number;

  /**
   * N+ junction sidewall capacitance in fF/µm (NMOS S/D sidewall).
   * Total C_sw = cjsw × junction_perimeter
   */
  nJunctionSidewallCapFfPerUm?: number;

  /**
   * N+ junction gate-edge capacitance in fF/µm (NMOS S/D at gate edge).
   * Total C_swg = cjswg × gate_width
   */
  nJunctionGateCapFfPerUm?: number;

  /**
   * P+ junction area capacitance in fF/µm² (PMOS source/drain to N-well).
   */
  pJunctionCapFfPerUm2?: number;

  /**
   * P+ junction sidewall capacitance in fF/µm (PMOS S/D sidewall).
   */
  pJunctionSidewallCapFfPerUm?: number;

  /**
   * P+ junction gate-edge capacitance in fF/µm (PMOS S/D at gate edge).
   */
  pJunctionGateCapFfPerUm?: number;

  // -------------------------------------------------------------------------
  // Gate Capacitance
  // -------------------------------------------------------------------------

  /**
   * Gate oxide capacitance in fF/µm² (Cox = εox / tox).
   * Total gate capacitance ≈ Cox × W × L
   */
  gateOxideCapFfPerUm2?: number;

  /**
   * Gate-to-source/drain overlap capacitance in fF/µm.
   * Total overlap C = Cov × W (per side)
   */
  gateOverlapCapFfPerUm?: number;
}

// ============================================================================
// Format-Agnostic Layer System (v6)
// ============================================================================

/**
 * Exports interface for format-specific layer/purpose mappings. GDS is not
 * here: it is a table of its own, `PDK.layerMap`.
 */
export interface Exports {
  /**
   * OpenAccess layer or purpose number.
   *
   * `uint32`, not `int`: OA's layer and purpose numbers are `oaUInt4`, and its
   * twelve reserved purposes are numbered from the TOP of that range
   * (`drawing` is `0xFFFFFFFF`). An `int` annotation rejected them, which is
   * how a truthful `drawing` entry failed validation while the wrong one
   * (`oa: 0`) passed. See `NoaLib/src/conv/purposes.ts` and gap G16.
   * @type uint32
   */
  oa?: number;
  /**
   * OASIS layer number
   * @type int
   */
  oasis?: number;
  /**
   * Other format-specific mappings (e.g., "lef", "def")
   */
  others?: Record<string, number>;
}

// ============================================================================
// Stream layer map (mirrors `crates/schema/src/current/layer_map.rs`)
// ============================================================================

/** One stream-out row: a layer/purpose, optionally narrowed by mask colour or cut size, and its GDS pair. */
export interface StreamRow {
  /**
   * The PDK layer's number (`PDKLayer.number`).
   * @type int
   */
  layer: number;
  /**
   * The purpose's number on that layer (`PDKLayerPurpose.number`).
   * @type int
   */
  purpose: number;
  /** GDS `[layer, datatype]`. */
  gds: [number, number];
  /** The mask colour this row is for, on a multi-patterned layer. */
  mask?: MaskColor;
  /** The via cut size this row is for, `[width, height]` in nm. */
  cutSize?: [number, number];
  /** Source columns kept verbatim, e.g. `cut` or `OASISLayerName:M1`. */
  extra?: string[];
  /** False for an alias: of several rows writing one GDS pair, the one read back leaves this true. Default true. */
  import?: boolean;
  /** False for a row that is only read: of several rows for one layer/purpose, the one written leaves this true. Default true. */
  export?: boolean;
  /** Where the row came from: `<file>:<line>`, or a design-rule citation. */
  source?: string;
}

/** A mask colour, numbered from 1. */
export interface MaskColor {
  /** @type int */
  color: number;
  locked?: boolean;
}

export type PurposeRole =
  | "drawing"
  | "pin"
  | "label"
  | "net"
  | "blockage"
  | "boundary"
  | "fill"
  | "dummy";

export interface LayerMapCoverage {
  unmapped: Array<{
    /** @type int */
    layer: number;
    /** @type int */
    purpose: number;
    reason: string;
  }>;
}

/**
 * PDK Layer Purpose (drawing, pin, label, etc.)
 * In v6, uses GenAlpha-native numbering with format-specific exports
 */
export interface PDKLayerPurpose {
  name: string; // Purpose name ("drawing", "pin", "label")
  /**
   * GenAlpha-assigned purpose number (format-agnostic)
   * @type int
   */
  number: number;
  /** What the purpose is for, when its name does not say so conventionally. */
  role?: PurposeRole;
  /**
   * Format-specific export mappings for datatypes/purposes
   */
  exports: Exports;
}

/**
 * PDK Layer Definition
 * In v6, uses GenAlpha-native numbering with format-specific exports
 */
export interface PDKLayer {
  name: string; // Layer name ("M1", "RX", "PC")
  /**
   * GenAlpha-assigned layer number (format-agnostic)
   * @type int
   */
  number: number;
  color?: Color;
  purposes: Array<PDKLayerPurpose>;
  category:
    | "metal"
    | "via"
    | "diffusion"
    | "poly"
    | "implant"
    | "mask"
    | "well"
    | "gate"
    | "contact"
    | "marker"
    | "other";
  /**
   * Format-specific export mappings for base layer numbers
   */
  exports?: Exports;
}

/** Front-end rules that placement needs, in nm. Absent means not filled, never zero. */
export interface Frontend {
  /** The contacted poly (gate) pitches the manual allows. */
  cpp?: number[];
  /** @type int */
  finPitch?: number;
  poly?: FrontendLayer;
  diffusion?: FrontendLayer;
  nwell?: FrontendLayer;
  /** The n+ / p+ select (implant) layers. */
  implants?: FrontendLayer[];
  /**
   * Latch-up: the largest distance from a device's diffusion to a well or substrate tap.
   * @type int
   */
  tapDistanceMax?: number;
  /** Where each value came from, keyed by its field path in this object. */
  drmSource?: Record<string, DrmCite>;
}

/** One front-end layer's base rules, in nm. */
export interface FrontendLayer {
  /** The layer's name in this PDK. */
  layer: string;
  /** @type int */
  width?: number;
  /** @type int */
  spacing?: number;
  /**
   * How far the layer must extend past the diffusion it covers.
   * @type int
   */
  enclosureOfDiffusion?: number;
}

/** What the manual does not state, so that an absent value never reads as zero. */
export interface DrmCoverage {
  missing: NotStated[];
}

export interface NotStated {
  /** `"frontend"`, `"stack:<id>/<layer>"` or `"via:<stack id>/<via>"`. */
  scope: string;
  field: string;
  /** "not stated", or "NEEDS REVIEW: <why>". */
  reason: string;
}
